import { DbUtils } from './dbUtils';
import { DbScope } from './dbScope';
import { CacheConfig } from './cacheConfig';

// 20261020: Customer/Supplier chat conversation cache - the room shows its last known messages at once while the
// server is asked (same idea as the Workplace/Task conver cache).
// - IndexedDB through DbUtils (key `u{id}.ccv_{roomId}` - per user, nothing in View-As mode) + a synchronous
//   in-memory mirror: an IndexedDB read can take seconds while the main thread is busy (measured when the first chat
//   cache was tried and rolled back, 2026-08-28), so the open path reads memory and only falls back to IndexedDB.
// - Only the newest messages of a room (a page that reaches the room's newest message), max maxMsgs() per room,
//   max maxRooms() rooms (least recently saved dropped). Every save rewrites the whole entry, so it must stay small.
// 20261022: limits are per-device settings (cacheConfig.ts), defaults 100 msgs x 100 rooms
const maxMsgs = () => CacheConfig.get("chatMsgs");
const maxRooms = () => CacheConfig.get("chatRooms");
const SAVE_DELAY_MS = 800; // bursts of changes (new messages, read marks) -> one write
const IDX_KEY = "ccvidx";
const entryKey = (roomId: any) => `ccv_${roomId}`;

export interface ChatConverCacheEntry {
  roomId: string | number;
  messages: any[];      // oldest first, no date dividers, only messages the server has
  firstMsgId: string | null;
  hasOlder: boolean;
  savedAt: number;      // ms
  lct?: string | null;  // room list lastChatTime when pre-fetched (unchanged -> no new pre-fetch needed)
}

const mem = new Map<string, ChatConverCacheEntry>(); // key = scope prefix + room id
const memKey = (roomId: any) => {
  const prefix = DbScope.prefix();
  return prefix ? prefix + roomId : null;
};
const saveTimers = new Map<string, any>();
let warmedPrefix: string | null = null;

// messages worth keeping: real server messages (no divider, no own message still being sent, no local bot notice)
const isCacheable = (m: any) =>
  m && m.type != "date-divider" && m.mId && m.conversationId != null && m.conversationId !== -1 && m.meta?.sent !== false;

const updateIndex = async (roomId: any, savedAt: number) => {
  const idx: Array<{ r: any; t: number }> = (await DbUtils.getOrSet(IDX_KEY, null)) || [];
  const next = [{ r: roomId, t: savedAt }, ...idx.filter(x => x.r + "" !== roomId + "")];
  const keep = next.slice(0, maxRooms());
  const drop = next.slice(maxRooms());
  await DbUtils.getOrSet(IDX_KEY, keep);
  for (const d of drop) {
    const k = memKey(d.r);
    if (k) mem.delete(k);
    await DbUtils.DeleteScoped(entryKey(d.r));
  }
};

export const ChatConverCache = {
  // synchronous: memory only
  getMem(roomId: any): ChatConverCacheEntry | null {
    const k = memKey(roomId);
    return k ? (mem.get(k) ?? null) : null;
  },

  // memory, else IndexedDB (fills memory)
  async get(roomId: any): Promise<ChatConverCacheEntry | null> {
    const k = memKey(roomId);
    if (!k) return null;
    const m = mem.get(k);
    if (m) return m;
    const e = await DbUtils.getOrSet(entryKey(roomId), null);
    if (e && Array.isArray(e.messages) && e.messages.length > 0) {
      mem.set(k, e);
      return e;
    }
    return null;
  },

  // load the cached rooms into memory once per user (call when the chat page opens)
  async warmUp(): Promise<void> {
    const prefix = DbScope.prefix();
    if (!prefix || warmedPrefix === prefix) return;
    warmedPrefix = prefix;
    try {
      const idx: Array<{ r: any; t: number }> = (await DbUtils.getOrSet(IDX_KEY, null)) || [];
      await Promise.all(idx.map(x => ChatConverCache.get(x.r)));
    } catch (e) {
      console.error(e);
    }
  },

  // keep the newest messages of a room; conv = state.chatUserConversations (must reach the newest message)
  save(roomId: any, messages: any[], firstMsgId: string | null, hasOlder: boolean | undefined, lct: string | null = null): void {
    const k = memKey(roomId);
    if (!k || roomId == null) return;
    let list = (messages || []).filter(isCacheable);
    if (list.length <= 0) return;
    let older = hasOlder === true;
    let first = firstMsgId ?? list[0].mId;
    const max = maxMsgs();
    if (list.length > max) {
      list = list.slice(list.length - max);
      older = true;
      first = list[0].mId;
    }
    const entry: ChatConverCacheEntry = { roomId, messages: list, firstMsgId: first, hasOlder: older, savedAt: Date.now(), lct };
    mem.set(k, entry); // memory at once (next open of this room paints it), disk a bit later
    const t = saveTimers.get(k);
    if (t) clearTimeout(t);
    saveTimers.set(k, setTimeout(() => {
      saveTimers.delete(k);
      if (memKey(roomId) !== k) return; // user changed meanwhile
      DbUtils.getOrSet(entryKey(roomId), entry)
        .then(() => updateIndex(roomId, entry.savedAt))
        .catch(e => console.error(e));
    }, SAVE_DELAY_MS));
  },

  // 20261022: rooms cached now for the current user (Settings > cache)
  async count(): Promise<number> {
    try {
      const idx = await DbUtils.getOrSet(IDX_KEY, null);
      return Array.isArray(idx) ? idx.length : 0;
    } catch (e) {
      return 0;
    }
  },

  clearMem(): void {
    saveTimers.forEach(t => clearTimeout(t));
    saveTimers.clear();
    mem.clear();
    warmedPrefix = null;
  },
};

DbScope.onClear(() => ChatConverCache.clearMem()); // logout / user switch (IndexedDB entries are removed by DbUtils)
